import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spinner,
  useDisclosure,
} from '@nextui-org/react';
import moment from 'moment';
import { useSearchParams } from 'next/navigation';
import type { SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  IconFireGenCampaign,
  IconInfoCardProcess,
  IconOptionCardTemplateProfile,
  IconRemixProcessing,
} from '@/assets';
import { Images } from '@/components';
import NoData from '@/components/NoData';
import Pagination from '@/components/pagination';
import RenderWhen from '@/components/RenderWhen';
import TooltipCustom from '@/components/tooltip-custom';
import ViewFullMedia from '@/components/view-media';
import RemixCommon from '@/layouts/remix-common';
import { useRouter } from '@/provider/RouterProvider';
import { actionHideArtProcessing, retryProcessingAsset } from '@/services';
import { deleteTemplate, publishTemplate } from '@/services/usecase';
import type { IAssetProcessing, STATUS_PROCESSING } from '@/types/asset';
import type { IUsecaseItem } from '@/types/usecase';

import { ProfileTabKey } from '..';
import { PAGE_SIZE } from './useProcessing';
import NsfwMark from '@/components/NsfwMark';
import { isVideoByUrl } from '@/utils';
import ReactPlayer from 'react-player';

interface ContentProcess {
  view: any;
  action: (item: IAssetProcessing) => any;
}

export interface StatusProcess {
  [STATUS_PROCESSING.Minting]: ContentProcess;
  [STATUS_PROCESSING.Minted]: ContentProcess;
  [STATUS_PROCESSING.MintFailed]: ContentProcess;
  [STATUS_PROCESSING.ViewSimilarity]: ContentProcess;
  [STATUS_PROCESSING.SimilarityChecking]: ContentProcess;
  [STATUS_PROCESSING.New]: ContentProcess;
  [STATUS_PROCESSING.Retry]: ContentProcess;
  [STATUS_PROCESSING.GenerateError]: ContentProcess;
  [STATUS_PROCESSING.GeneratePending]: ContentProcess;
  [STATUS_PROCESSING.Transfering]: ContentProcess;
}

const ListProcessing: React.FC<{
  data: IAssetProcessing[];
  statusProcess: StatusProcess;
  loading: boolean;
  setProcessData: React.Dispatch<SetStateAction<IAssetProcessing[]>>;
  totalItems: number;
}> = ({ data, statusProcess, loading, setProcessData, totalItems }) => {
  const router = useRouter();
  const searchParam = useSearchParams();
  const page = searchParam.get('page') ?? 1;
  const [remixConfig, setRemixConfig] = useState<IUsecaseItem | null>(null);
  const useModalRemix = useDisclosure({
    onClose() {
      document.body.style.removeProperty('overflow');
      setRemixConfig(null);
    },
  });
  const { onOpen: onOpenModel } = useModalRemix;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [currentType, setCurrentType] = useState('');

  const publishAsset = async (id: number) => {
    const res = await publishTemplate([id]);
    if (res) {
      toast.success('Published successfully');
    } else {
      toast.error(`Published error`);
    }
  };

  const deleteAsset = async (id: number) => {
    const res = await deleteTemplate([id]);
    if (res) {
      toast.success(`Deleted successfully`);
      setProcessData(data.filter((item) => item.id !== id));
    } else {
      toast.error(`Deleted error`);
    }
  };

  const openModal = (src: any, type: any) => {
    setIsModalOpen(true);
    setCurrentImage(src);
    setCurrentType(type);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentImage('');
    setCurrentType('');
  };

  const handelHideArtProcessing = async (id: number) => {
    setProcessData(data.filter((item) => item.id !== id));
    await actionHideArtProcessing(id);
  };

  const handleRetryImg = async (e: any, item: any) => {
    e.stopPropagation();
    if (item?.model?.isWorkflow) {
      router.push(`/showcase/${item?.model?.id}`);
      return;
    }
    if (item.status === 0) {
      const res = await retryProcessingAsset(item.id);
      if (res.status === 201) {
        setProcessData((data) =>
          data.map((asset) => (asset.id === item.id ? { ...asset, status: 2 } : asset)),
        );
      }
      return;
    }

    setRemixConfig({ ...item, processingAsset: { modelConfig: item.modelConfig } });
    document.body.style.overflow = 'hidden';
    onOpenModel();
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isModalOpen]);

  return (
    <div>
      {remixConfig && <RemixCommon useModalRemix={useModalRemix} remixConfig={remixConfig} />}
      <div className="text-[15px]">
        <RenderWhen>
          <RenderWhen.If isTrue={data?.length > 0}>
            <div className="grid grid-cols-5 gap-4 2xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:gap-2">
              {data.map((item) => (
                <div
                  aria-checked={isVideoByUrl(item.url || item.originalUrl)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item?.originalUrl) {
                      openModal(item.originalUrl, item.mimeType);
                    }
                  }}
                  className="group bg-black relative h-[calc(100vw/4)] max-h-[400px] min-h-[300px] cursor-pointer overflow-hidden rounded-[16px] shadow-lg shadow-[rgba(199,205,231,0.55)]  sm:rounded-[10px]"
                >
                  {isVideoByUrl(item.url || item.originalUrl) ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <ReactPlayer
                        className="!w-full !h-auto"
                        controls
                        url={item.url || item.originalUrl}
                      />
                    </div>
                  ) : (
                    <Images
                      src={item.url || item.originalUrl}
                      alt="usecase"
                      className="h-full w-full cursor-pointer object-cover duration-300 group-hover:scale-110"
                    />
                  )}
                  {item.isNsfw && <NsfwMark url={item.url || item.originalUrl} />}
                  <div aria-checked={isVideoByUrl(item.url || item.originalUrl)} className="absolute bottom-0 left-0 z-10 h-[100px] aria-checked:h-[30px] w-full bg-gradient-to-t from-black/80 to-black/0" />
                  <div className="absolute inset-x-0 bottom-0 z-20 p-2">
                    {item.id && (
                      <>
                        <div className="flex items-center justify-between">
                          <h6 className="flex truncate text-[14px] font-medium text-white drop-shadow">
                            #{item.id}
                            {item?.campaign?.name && (
                              <span className="pl-[2px]">
                                <IconFireGenCampaign />
                              </span>
                            )}
                          </h6>
                          <TooltipCustom
                            placement="top"
                            content={
                              <div className="flex max-w-[290px] flex-col gap-3 px-2 py-4">
                                <div className="flex justify-between gap-4">
                                  <span className="text-[14px] font-semibold text-gray-600">
                                    Model
                                  </span>
                                  <span className="flex cursor-pointer items-center text-[16px] font-semibold text-orange-500">
                                    {item.model?.name}
                                  </span>
                                </div>
                                {item?.campaign?.name && (
                                  <div className="flex justify-between gap-4">
                                    <span className="text-[14px] font-semibold text-gray-600">
                                      Campaign
                                    </span>
                                    <span className="flex items-center text-[16px] font-semibold text-orange-500">
                                      {item?.campaign?.name ? (
                                        <span
                                          style={{
                                            background:
                                              'linear-gradient(90deg, #DA613A 0%, #EB476B 42.19%, #7D3DDC 96.87%)',
                                            WebkitTextFillColor: 'transparent',
                                            WebkitBackgroundClip: 'text',
                                          }}
                                          className="relative z-[1] text-[14px] font-semibold"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            router.push(`/campaign/${item?.campaignId}`);
                                          }}
                                        >
                                          {item?.campaign.tag}
                                        </span>
                                      ) : (
                                        <span className="ml-5 font-semibold text-[14]">---</span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between gap-4">
                                  <span className="text-[14px] font-semibold text-gray-600">
                                    Date created
                                  </span>
                                  <span className="text-[16px]">
                                    {moment(item.createdAt).format('ll')}
                                  </span>
                                </div>
                              </div>
                            }
                          >
                            <div className="cursor-pointer pl-[6px] text-white">
                              <IconInfoCardProcess />
                            </div>
                          </TooltipCustom>
                        </div>

                        {item.originalUrl && !isVideoByUrl(item.url || item.originalUrl) && <div>{statusProcess[item.status].action(item)}</div>}
                      </>
                    )}
                  </div>

                  <p className="absolute left-2 top-2 z-[10] flex w-fit items-center justify-self-end rounded-[100px] bg-orange-50 p-0 text-[14px] backdrop-blur-[2px] transition-all sm:hidden">
                    {statusProcess[item.status].view}
                  </p>

                  <div
                    className="absolute right-2 top-2 z-[20] flex flex-col gap-[0px] md:right-1 md:top-1"
                    onClick={(event) => {
                      event.preventDefault();
                    }}
                  >
                    <p
                      className="flex scale-75 items-center text-[14px] font-semibold text-white sm:text-[13px]"
                      onClick={(e) => {
                        handleRetryImg(e, item);
                      }}
                    >
                      <IconRemixProcessing />
                    </p>
                    <div className="">
                      <Dropdown>
                        <DropdownTrigger>
                          <div
                            className="flex scale-75 cursor-pointer items-center justify-end"
                            onClick={(event) => {
                              event.preventDefault();
                            }}
                          >
                            <IconOptionCardTemplateProfile />
                          </div>
                        </DropdownTrigger>
                        <DropdownMenu
                          aria-label="Static Actions"
                          onAction={(key) => {
                            if (key === 'hide') {
                              handelHideArtProcessing(item.id);
                            } else if (key === 'publish') {
                              publishAsset(item.id);
                            } else {
                              deleteAsset(item.id);
                            }
                          }}
                        >
                          <DropdownItem key="hide">
                            {item.hide === false && 'Hide'}
                            {item.hide === true && 'Unhide'}
                          </DropdownItem>

                          {/* <DropdownItem key="edit">Edit</DropdownItem> */}
                          <DropdownItem key="delete" className="text-danger" color="danger">
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </RenderWhen.If>
          <RenderWhen.If isTrue={loading && data?.length === 0}>
            <div className="flex h-[350px] items-center justify-center">
              <Spinner label="Loading..." />
            </div>
          </RenderWhen.If>
          <RenderWhen.If isTrue={!loading && data?.length === 0}>
            <NoData className="h-[350px]" />
          </RenderWhen.If>
        </RenderWhen>
        <div className="grid justify-items-center">
          <Pagination
            className="mt-5 sm:mb-5"
            startPage={Number(page)}
            total={totalItems}
            pageSize={PAGE_SIZE}
            queryPrams={[{ key: 'tab', value: ProfileTabKey.processing }]}
            queryOptions={{ scroll: false }}
          />
        </div>
      </div>
      {currentType.includes('image') && (
        <ViewFullMedia isOpen={isModalOpen} onClose={closeModal} url={currentImage} />
      )}
    </div>
  );
};

export default ListProcessing;
